'use client';

/**
 * The keyboard and commit contract every **composite popover editor** on this
 * treatment shares: `address`, `name`, `bankAccount`, `marketFigure`,
 * `multiSelect`, `principalAndMembers` and `valueWithConfirmation` (VALE-1321).
 *
 * Those seven are one shape: a value made of several parts, edited in a popover
 * that opens over the cell the read-out occupied. Before this hook each of them
 * hand-wrote the same three-part contract, and the duplication was already
 * visible in the source. `attribute-multi-select-editor.tsx`'s own comment
 * described its guard as *"the identical guard `attribute-address-editor.tsx`
 * and `attribute-name-editor.tsx` both use"*. Seven copies of one contract is
 * seven places for it to drift, and that is exactly what had happened: all
 * seven committed on dismiss and reverted on `Escape`, and **not one of them
 * did anything at all on `Enter`**, while the single-line field, the money
 * field and the `selectWithCustom` field all committed on it. The grid taught a
 * gesture on its commonest row and then ignored it on eight others.
 *
 * So the contract lives here once, and a new composite editor inherits all of
 * it by construction rather than by its author remembering three separate
 * behaviours. `attribute-grid.test.tsx`'s Enter registry is the other half:
 * membership is derived from the editor union, so a kind added later has to
 * declare its entitlement rather than being silently exempt.
 *
 * **Eight call sites, not seven** (VALE-1710). The seven `kind`s above are the
 * editors; the eighth is `balance-sheet/current-view/inventory-value-popover.tsx`,
 * the shell the Current View sets an inventory figure through, which is not a
 * `kind` at all and binds this contract directly. That is why
 * `__tests__/attribute-popover-confirm.test.tsx` derives its membership by
 * scanning for call sites across `components/` rather than from the editor
 * union: the union cannot see the eighth, and a scan narrowed to this folder
 * would report the seven and look complete.
 *
 * **This module is `.tsx` because the contract now includes a rendered control**
 * — see {@link confirm}. Everything a member needs is returned from one hook, so
 * the member decides only where to put it.
 *
 * ## The contract
 *
 * · **Dismissing commits.** Clicking away is the gesture the treatment has
 *   always committed on (`components/attributes/CLAUDE.md` §2), and a popover's
 *   dismissal is its blur.
 * · **`Escape` reverts**, and must not then also commit (see
 *   {@link settledRef}).
 * · **`Enter` commits and closes**, unless an inner control has already
 *   answered for it. See {@link commitsOnEnter}, which is where all the
 *   subtlety is.
 * · **Visible controls do the same things**, so none of the three above has to
 *   be known in advance: Done commits, Cancel reverts. See {@link confirm}.
 *
 * `Tab` is deliberately **not** here. The grid's field-to-field chaining stops
 * at a composite row on purpose: tabbing out of a half-typed address to the
 * next card is the opposite of helpful, so `Tab` moves between the parts
 * natively. That was `attribute-address-editor.tsx`'s own decision and this
 * hook preserves it rather than quietly widening it.
 */

import { useEffect, useRef } from 'react';
import type React from 'react';
import { AttributePopoverConfirm } from './attribute-popover-confirm';
import type { AttributeDraft } from './attribute-types';

/**
 * Does this `Enter` mean *"commit the editor"*?
 *
 * The handler sits on the popover's content, so it sees every `Enter` pressed
 * anywhere inside the panel. Most of them are already somebody's: the whole
 * job here is to commit on the ones that are nobody's, and only those. Each
 * clause below corresponds to a control one of the seven editors actually
 * renders.
 */
export function commitsOnEnter(event: React.KeyboardEvent): boolean {
  if (event.key !== 'Enter') return false;

  /*
    A modified `Enter` is a different gesture, not this one. `Shift+Enter` in
    particular is the newline a reader reaches for in the bank account's
    verification note, and swallowing it to commit would be the same defect as
    committing on a plain `Enter` in the long-text row.
  */
  if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
    return false;
  }

  /*
    **An inner control already acted on it.** This is the load-bearing clause,
    and `defaultPrevented` is the right question because the three controls
    that consume `Enter` in these panels all announce it that way:

    · `cmdk`'s list (`multiSelect`, and `principalAndMembers`' member list)
      calls `preventDefault()` and then dispatches select on the highlighted
      item.
    · `AddressSearch` calls `preventDefault()` when a suggestion is active, so
      `Enter` picks the suggestion rather than closing the editor over a
      half-chosen address.
    · Radix's `Select` trigger consumes it to open the options.

    Bubble phase rather than capture is therefore deliberate: an inner handler
    runs first and gets to claim the keypress, and this one stands down. A
    capture-phase handler would take `Enter` away from all three.

    None of that is theoretical for `cmdk` today. With no `CommandInput` and
    no focusable items, Radix parks focus on the popover content *above*
    `cmdk-root`, so its handler never runs and its arrow-key navigation is
    dead too (a pre-existing gap, not this one's). The clause is here because
    that is a fact about the current markup rather than about the contract, and
    the first `CommandInput` added to one of these panels would restore it.
  */
  if (event.defaultPrevented) return false;

  // Mid-composition, `Enter` is the IME accepting a candidate.
  if (event.nativeEvent.isComposing) return false;

  const target = event.target as HTMLElement | null;
  if (!target) return true;

  /*
    A textarea is prose, so `Enter` is a newline. This is the identical exemption the
    `multiline` row keeps, for the identical reason (see
    `AttributeLongTextEditor`). The bank account's verification note is the one
    of these panels that has one.
  */
  if (target.tagName === 'TEXTAREA') return false;

  /*
    Something `Enter` natively activates. A `<button>` fires its click from
    `Enter` **without** marking the keydown as handled, so `defaultPrevented`
    above cannot see it, hence a second, structural clause. `role="option"`
    and `role="checkbox"` cover the list rows, and `role="combobox"` the select
    triggers, for the case where Radix stops announcing consumption.
  */
  if (
    target.closest(
      'button,a,[role="option"],[role="checkbox"],[role="combobox"]'
    )
  ) {
    return false;
  }

  return true;
}

/**
 * Wire one composite popover editor's `Popover` and `PopoverContent` to the
 * contract above.
 *
 * `draft` is the value as of this render, the same value the hand-written
 * `onOpenChange` closures committed, so the handlers see whatever the last
 * keystroke drafted rather than the value the editor opened with.
 */
export function useAttributePopoverCommit({
  draft,
  onRevert,
  onCommit,
  claimEnterBeforeList = false,
  confirmInset = false
}: {
  draft: AttributeDraft;
  onRevert: () => void;
  onCommit: (value: AttributeDraft) => void;
  /**
   * **Take `Enter` in the capture phase, before an inner `cmdk` list can claim
   * it**: the two editors whose panel is a checkable list (`multiSelect`, and
   * `principalAndMembers`' member half).
   *
   * The default is bubble phase, and for the five editors whose panels hold
   * real inputs that is exactly right: an inner control gets first refusal and
   * this handler stands down on `defaultPrevented` (see
   * {@link commitsOnEnter}). A `cmdk` list breaks that arrangement, because it
   * claims `Enter` *whenever it has focus* and it acquires focus from any click
   * on a row. So after the reader has ticked a single option, a bubbling
   * `Enter` would be swallowed to re-toggle the highlighted row and the editor
   * could never be committed from the keyboard at all. That is the state these
   * two rows were in, and it is why "just add `Enter`" was not enough for them.
   *
   * Capture runs outer-to-inner, so claiming it here means `cmdk` never sees
   * it. The toggle gesture it loses is handed to `Space`, which is what ARIA's
   * multi-select listbox pattern uses for exactly this. See each editor's own
   * `Command` handler.
   */
  claimEnterBeforeList?: boolean;
  /**
   * **The panel supplies no padding of its own**, so {@link confirm} has to
   * carry it — see {@link ATTRIBUTE_POPOVER_CONFIRM_ROW_INSET}.
   *
   * One panel is in this state today: `multiSelect`, whose `Command` list brings
   * its own padding and which therefore takes the shared width with `p-0` rather
   * than the panel wholesale. A flag rather than something derived, for the same
   * reason `claimEnterBeforeList` is one: the hook cannot see the classes its
   * caller put on `PopoverContent`, and the caller already knows.
   */
  confirmInset?: boolean;
}) {
  /**
   * `Escape` must revert and *not* commit, but Radix fires
   * `onOpenChange(false)` after `onEscapeKeyDown` for the same keypress, so
   * without this the revert would be immediately followed by a commit of the
   * reverted draft.
   *
   * **Only `Escape` sets it, never the `Enter` commit.** An accepted commit
   * closes the editor from the grid, which unmounts the popover, and unmounting
   * does not fire `onOpenChange`, so there is no second commit to guard
   * against. A *refused* commit deliberately leaves the panel open (the grid
   * keeps the typing so the reader can correct it), and a guard set here would
   * then swallow the commit of that correction when the panel is finally
   * dismissed.
   */
  const revertedRef = useRef(false);

  /**
   * **Settled by us, on the pointerdown, rather than by Radix a tick later**
   * (VALE-1617).
   *
   * Dismissing commits (`components/attributes/CLAUDE.md` §2), and Radix
   * normally delivers that dismissal. It dispatches the outside-interaction
   * *deferred*, though — measured, not assumed — and one tick is enough for the
   * click to have opened a **page-covering modal** on top. Once a Sheet is
   * mounted above it, the popover's layer is no longer the topmost and its
   * dismissal is suppressed: the editor is left open underneath the drawer,
   * holding typing that was never committed, which the reviewer reads as a save
   * that silently failed.
   *
   * Reported exactly that way: an address typed to `35 Regents Streetttt`, a
   * click on the row, and a drawer showing `35 Regents Street` with the popover
   * still floating over the dimmed page behind it.
   *
   * So the contract stops depending on that delivery. A capture-phase
   * `pointerdown` outside the panel commits and closes through the same
   * `onCommit` the grid wires to `commitAndClose` — before anything can mount
   * above. The click itself is untouched: the drawer still opens, which is what
   * the reader asked for by clicking the row.
   */
  const settledRef = useRef(false);
  const latestRef = useRef({ draft, onCommit });
  latestRef.current = { draft, onCommit };

  /**
   * **The latch clears the moment the reader types again.**
   *
   * A keystroke produces a new draft object, so an identity check is the whole
   * test: the same draft means this dismissal is the one our own commit caused
   * and must not commit twice, a different draft means the reader has moved on
   * and it must.
   *
   * **Belt and braces, not a fix for an observed failure.** The hazard it
   * guards is real in principle — a refused commit deliberately leaves the
   * panel open for the reader to correct (see {@link revertedRef}), and a
   * permanently latched guard would swallow that correction. It is not
   * reachable today, because the grid remounts the editor after a refusal and
   * the fresh hook starts unlatched; a test written against it passes with the
   * clear removed. That remount is an implementation detail of the grid rather
   * than a promise to this hook, which is why the clear stays.
   */
  const settledDraftRef = useRef<AttributeDraft | null>(null);
  if (settledRef.current && draft !== settledDraftRef.current) {
    settledRef.current = false;
  }

  useEffect(() => {
    const onPointerDown = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target?.closest) return;
      /* Inside the panel is not a dismissal. */
      if (target.closest('[data-radix-popper-content-wrapper]')) return;
      if (settledRef.current) return;
      settledRef.current = true;
      settledDraftRef.current = latestRef.current.draft;
      latestRef.current.onCommit(latestRef.current.draft);
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
  }, []);

  return {
    /**
     * **The visible way to finish an edit** (VALE-1710). Render it as the LAST
     * child of the `PopoverContent`, and render nothing else for it — the label,
     * the size, the alignment and the commit path all live here, so eight panels
     * cannot end up with eight slightly different buttons.
     *
     * Every gesture in this contract already commits, and not one of them was
     * visible: a reader who had just typed an address had nothing on screen
     * telling them how to finish, and the natural worry is the opposite of the
     * truth — that clicking away would discard the typing rather than save it.
     * So this is an **addition, not a replacement**. Dismissing still commits,
     * `Escape` still reverts, `Enter` still commits where it does today.
     *
     * **It routes through the same `onCommit`**, which is what makes it behave
     * the same way rather than merely look like it does: the grid's `validate`
     * gets its say, and a refused commit leaves the panel open with the typing
     * intact so it can be corrected (`components/attributes/CLAUDE.md` §6). A
     * control that closed the panel itself would have been a third commit path
     * with its own refusal behaviour, which is the drift this whole module
     * exists to stop.
     *
     * **It cannot double-commit, and the reason is structural rather than
     * lucky.** The click is a `pointerdown` *inside* the panel, so the
     * outside-pointerdown handler above stands down; and a keyboard activation
     * is an `Enter` on a `<button>`, which {@link commitsOnEnter} already
     * declines by its `target.closest('button,…')` clause. Both routes arrive
     * here exactly once.
     *
     * **Cancel sits beside it** (VALE-2080, reversing VALE-1710's decision to
     * leave it out). `Escape` was the only way to throw an edit away and
     * nothing on screen said so. Cancel calls **the same `onRevert` `Escape`
     * does**, which restores every part to what the panel opened with and
     * closes the editor itself. Clicking outside still **commits**, a deliberate
     * choice made on that ticket: it is the commonest gesture here and the one
     * VALE-1617 made reliable. So Cancel is a visible `Escape`, not the start of a dialog
     * model.
     *
     * **It does not set {@link revertedRef}**, which is the one way it differs
     * from `Escape`. A click fires no Radix dismissal to guard against, and on
     * the inventory popover, whose `open` is controlled and whose hook outlives
     * the panel, a latch set here would still be set the next time the panel
     * opened and would swallow that edit's dismiss-commit.
     *
     * It cannot commit by accident either, for the same structural reasons Done
     * cannot double-commit: its `pointerdown` is inside the panel, and an
     * `Enter` on it is an `Enter` on a `<button>`, which
     * {@link commitsOnEnter} declines, leaving the native click as its only
     * effect.
     */
    confirm: (
      <AttributePopoverConfirm
        inset={confirmInset}
        onConfirm={() => onCommit(draft)}
        onCancel={onRevert}
      />
    ),
    /** Spread onto the `Popover`. */
    popover: {
      defaultOpen: true,
      onOpenChange: (open: boolean) => {
        if (open) return;
        if (revertedRef.current) {
          revertedRef.current = false;
          return;
        }
        /*
          Already committed by the outside-pointerdown handler above, which runs
          first and does not wait for Radix. Without this guard the same edit
          would commit twice — and on a refused commit the second one would
          re-send the value the first was refused for.
        */
        if (settledRef.current) return;
        onCommit(draft);
      }
    },
    /** Spread onto the `PopoverContent`. */
    content: {
      onEscapeKeyDown: (event: KeyboardEvent) => {
        /*
          Stop the sheet seeing it too. The drawer's ref-based guard covers the
          grid's inputs, and this popover is a second dismissable layer above
          it.
        */
        event.preventDefault();
        revertedRef.current = true;
        onRevert();
      },
      [claimEnterBeforeList ? 'onKeyDownCapture' : 'onKeyDown']: (
        event: React.KeyboardEvent
      ) => {
        if (!commitsOnEnter(event)) return;
        event.preventDefault();
        onCommit(draft);
      }
    } as {
      onEscapeKeyDown: (event: KeyboardEvent) => void;
      onKeyDown?: (event: React.KeyboardEvent) => void;
      onKeyDownCapture?: (event: React.KeyboardEvent) => void;
    }
  };
}

/**
 * `Space` toggles the highlighted row in a `cmdk` list, the gesture `Enter`
 * gives up when {@link useAttributePopoverCommit}'s `claimEnterBeforeList` is
 * set (VALE-1321).
 *
 * Pass as `<Command onKeyDown={commandListToggleOnSpace}>`: `cmdk` calls this
 * prop *before* its own key switch and then skips that switch when the event
 * has been prevented, which is the documented seam for overriding it.
 *
 * The highlighted row is `cmdk`'s own `aria-selected` item, and clicking it is
 * how every other path in these panels toggles a row, so this drives the same
 * `onSelect` a mouse does rather than reaching for `cmdk`'s internal select
 * event name.
 */
export function commandListToggleOnSpace(event: React.KeyboardEvent) {
  if (event.key !== ' ' || event.defaultPrevented) return;
  const highlighted = event.currentTarget.querySelector<HTMLElement>(
    '[cmdk-item][aria-selected="true"]'
  );
  if (!highlighted) return;
  // Before the click, so `cmdk` does not also scroll or re-highlight, and so a
  // `Space` in a list never scrolls the panel underneath it.
  event.preventDefault();
  highlighted.click();
}
